import React, { useState, useRef } from 'react';
import { getCalAvatar } from '../config/calAvatars';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { calMarkdownComponents } from './CalMarkdown';
import { API_ENDPOINT } from '../config';
import { useAuth0 } from '@auth0/auth0-react';
import { useCalWebSocket } from '../contexts/CalWebSocketContext';
import { useCalChat } from '../contexts/CalChatContext';
import { CalWebSocketMessage } from '../services/calWebSocketService';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'cal';
  timestamp: Date;
  markdown?: boolean;
  attachedDocument?: {
    fileName: string;
    documentId: string;
  };
}

interface UploadedDocument {
  documentId: string;
  fileName: string;
  uploadTime: Date;
  status: 'uploading' | 'uploaded' | 'analyzing' | 'analyzed' | 'updating' | 'updated';
  gapAnalysis?: any;
  selectedGaps?: string[];
  updatedDocumentId?: string;
  updatedDocumentUrl?: string;
  trackChangesPreview?: string;
}

interface Gap {
  gap_id: string;
  description: string;
  standard: string;
  recommendation: string;
  severity: 'critical' | 'major' | 'minor';
  can_auto_fix?: boolean;
  current_state?: string;
  required_state?: string;
}

interface GeneratedReport {
  id: string;
  title: string;
  type: 'checklist' | 'gap_analysis' | 'audit' | 'procedure';
  generatedAt: Date;
  pdfUrl: string;
  fileSize?: number;
  pages?: number;
  status: 'generating' | 'ready' | 'error';
}

const CalComplianceTab: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  // Use shared Cal chat context
  const {
    messages: contextMessages,
    sessionId,
    addMessage,
    updateMessage,
    setActiveView,
    clearMessages,
    activeView
  } = useCalChat();

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Cal is analyzing...');
  const [queryStartTime, setQueryStartTime] = useState<number>(0);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedGapsDialog, setSelectedGapsDialog] = useState<{open: boolean, documentId?: string}>({open: false});
  const [trackChangesDialog, setTrackChangesDialog] = useState<{open: boolean, preview?: string}>({open: false});
  const [selectedGapsForFix, setSelectedGapsForFix] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [currentChunk, setCurrentChunk] = useState('');
  const currentChunkRef = useRef<string>('');
  // Track the current active document for the session
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [activeDocumentName, setActiveDocumentName] = useState<string | null>(null);
  // Track generated PDF reports
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);

  // Convert context messages to local ChatMessage format
  const messages: ChatMessage[] = contextMessages.map(msg => ({
    id: msg.id,
    text: msg.text,
    sender: msg.sender,
    timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
    markdown: msg.text.includes('##') || msg.text.includes('**'),
    attachedDocument: msg.attachedDocument
  }));
  // Track timeout for clearing when response arrives
  const queryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { sendMessage: sendWebSocketMessage, isConnected, addMessageHandler } = useCalWebSocket();

  // Set active view when component mounts/unmounts
  React.useEffect(() => {
    setActiveView('compliance');
    return () => setActiveView('none');
  }, [setActiveView]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update loading message periodically when Cal is thinking
  React.useEffect(() => {
    if (isTyping && queryStartTime > 0) {
      const messages = [
        { time: 0, text: 'Cal is analyzing...' },
        { time: 3000, text: 'Cal is reviewing the requirements...' },
        { time: 6000, text: 'Cal is checking API standards...' },
        { time: 9000, text: 'Cal is searching knowledge base...' },
        { time: 12000, text: 'Cal is compiling your answer...' },
        { time: 15000, text: 'Cal is formatting the response...' },
        { time: 20000, text: 'Cal is finalizing details...' },
        { time: 25000, text: 'Almost there...' }
      ];

      const interval = setInterval(() => {
        const elapsed = Date.now() - queryStartTime;
        const currentMessage = messages
          .filter(m => m.time <= elapsed)
          .pop()?.text || 'Cal is analyzing...';
        setLoadingMessage(currentMessage);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isTyping, queryStartTime]);

  // Handle WebSocket messages
  const handleWebSocketMessage = React.useCallback((data: CalWebSocketMessage) => {
    // Only handle messages when this tab is active
    if (activeView !== 'compliance') {
      return;
    }

    switch (data.type) {
      case 'status':
        // Show thinking status or progress update
        setIsTyping(true);

        // Clear timeout when we receive a status update (query is being processed)
        if (queryTimeoutRef.current) {
          clearTimeout(queryTimeoutRef.current);
          queryTimeoutRef.current = null;
        }

        // If this is a specific progress message, show it to the user
        if (data.message &&
            (data.message.includes('Processing') ||
             data.message.includes('Extracting') ||
             data.message.includes('Analyzing'))) {
          const statusMessage: ChatMessage = {
            id: `msg-${Date.now()}-status`,
            text: `📊 ${data.message}`,
            sender: 'cal',
            timestamp: new Date(data.timestamp)
          };
          // Remove previous status messages and add new one
          // Note: We can't easily remove messages from shared context, so just add the new status
          addMessage({
            text: statusMessage.text,
            sender: 'cal',
            timestamp: new Date(data.timestamp)
          });
        }
        break;

      case 'chunk':
        // Append chunk to current message
        const newChunk = currentChunkRef.current + (data.content || '');
        currentChunkRef.current = newChunk;
        setCurrentChunk(newChunk);
        break;

      case 'complete':
        // Clear timeout when response is complete
        if (queryTimeoutRef.current) {
          clearTimeout(queryTimeoutRef.current);
          queryTimeoutRef.current = null;
        }

        // Regular complete message
        const completeMessage: ChatMessage = {
          id: `msg-${Date.now()}-cal`,
          text: data.message || data.response || currentChunkRef.current,
          sender: 'cal',
          timestamp: new Date(data.timestamp),
          markdown: true
        };
        addMessage({
          text: completeMessage.text,
          sender: completeMessage.sender,
          timestamp: completeMessage.timestamp
        });
        setCurrentChunk('');
        currentChunkRef.current = '';
        setIsTyping(false);
        setQueryStartTime(0); // Reset query start time

        // Check if this was a document analysis response
        if (data.analysis_complete) {
          // This is a completed analysis from the async analyzer
          const docName = data.document_name;
          console.log('Analysis complete for document:', docName);

          // Find the document that's currently being analyzed
          const attachedDoc = uploadedDocuments.find(d =>
            d.fileName === docName || d.status === 'analyzing'
          );

          if (attachedDoc) {
            console.log('Updating document status to analyzed:', attachedDoc.documentId);
            setUploadedDocuments(prev =>
              prev.map(d => d.documentId === attachedDoc.documentId
                ? { ...d, status: 'analyzed', gapAnalysis: { response: data.message || currentChunk } }
                : d
              )
            );
          } else {
            console.log('Could not find document to update:', docName);
          }
        } else if (data.message && data.message.includes('I\'ve started analyzing')) {
          // Analysis has been triggered async, keep document in analyzing state
          // The actual results will come later
        } else {
          // Regular message completion - check if it was an analysis
          const attachedDoc = uploadedDocuments.find(d => d.status === 'analyzing');
          if (attachedDoc && (data.message || currentChunk).includes('Gap Analysis')) {
            setUploadedDocuments(prev =>
              prev.map(d => d.documentId === attachedDoc.documentId
                ? { ...d, status: 'analyzed', gapAnalysis: { response: data.message || currentChunk } }
                : d
              )
            );
          }
        }
        break;

      case 'pdf_generated':
        // Handle PDF generation notification
        if (data.report_id && data.pdf_url) {
          const newReport: GeneratedReport = {
            id: data.report_id,
            title: data.title || 'Cal Compliance Report',
            type: data.report_type || 'checklist',
            generatedAt: new Date(data.timestamp),
            pdfUrl: data.pdf_url,
            fileSize: data.metadata?.file_size,
            pages: data.metadata?.pages,
            status: 'ready'
          };

          setGeneratedReports(prev => [newReport, ...prev]);

          // Show notification message
          const notificationMessage: ChatMessage = {
            id: `msg-${Date.now()}-pdf-notification`,
            text: `📄 ${data.title} has been generated and is available in the Gap Analysis Reports section.`,
            sender: 'cal',
            timestamp: new Date(data.timestamp)
          };
          addMessage({
            text: notificationMessage.text,
            sender: notificationMessage.sender,
            timestamp: notificationMessage.timestamp
          });
        }
        break;

      case 'timeout':
      case 'error':
        const errorMessage: ChatMessage = {
          id: `msg-${Date.now()}-cal`,
          text: data.message || 'An error occurred. Please try again.',
          sender: 'cal',
          timestamp: new Date(data.timestamp)
        };
        addMessage({
          text: errorMessage.text,
          sender: errorMessage.sender,
          timestamp: errorMessage.timestamp
        });
        setCurrentChunk('');
        setIsTyping(false);

        // Reset document status if analyzing
        setUploadedDocuments(prev =>
          prev.map(d => d.status === 'analyzing'
            ? { ...d, status: 'uploaded' }
            : d
          )
        );
        break;
    }
  }, [activeView, addMessage]);

  // Register WebSocket message handler
  React.useEffect(() => {
    const cleanup = addMessageHandler(handleWebSocketMessage);
    return cleanup;
  }, [addMessageHandler, handleWebSocketMessage]);

  const sendMessage = async (text: string, explicitDocumentId?: string) => {
    // Check if any document is currently being analyzed
    const analyzingDoc = uploadedDocuments.find(d => d.status === 'analyzing');

    // Use the explicit document ID if provided (from analyze button),
    // but if a document is already analyzing and this isn't an explicit analyze request,
    // don't attach any document to avoid duplicate analysis
    let attachedDocumentId = explicitDocumentId;
    let attachedDocumentName: string | undefined = undefined;

    if (!explicitDocumentId && !analyzingDoc) {
      // Only attach the active document if nothing is currently analyzing
      attachedDocumentId = activeDocumentId || undefined;
      attachedDocumentName = activeDocumentName || undefined;
    } else if (explicitDocumentId) {
      // Get the name for the explicit document
      const doc = uploadedDocuments.find(d => d.documentId === explicitDocumentId);
      attachedDocumentName = doc?.fileName;
    }

    // If we're analyzing a document, update its status
    if (attachedDocumentId && text.toLowerCase().includes('analyze')) {
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === attachedDocumentId
          ? { ...d, status: 'analyzing' }
          : d
        )
      );
    }
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      text,
      sender: 'user',
      timestamp: new Date(),
      attachedDocument: attachedDocumentId ?
        uploadedDocuments.find(d => d.documentId === attachedDocumentId) :
        undefined
    };

    // Convert local attachedDocument to shared context format
    const attachedDoc = userMessage.attachedDocument ? {
      ...userMessage.attachedDocument,
      s3Key: userMessage.attachedDocument.documentId, // Use documentId as s3Key
      status: 'uploaded' as const
    } : undefined;

    addMessage({
      text: userMessage.text,
      sender: userMessage.sender,
      timestamp: userMessage.timestamp,
      attachedDocument: attachedDoc
    });
    setIsTyping(true);
    setCurrentChunk(''); // Reset chunk for new message
    setQueryStartTime(Date.now()); // Track when query started for loading messages
    setLoadingMessage('Cal is analyzing...'); // Reset loading message

    const context = {
      userName: user?.name,
      userEmail: user?.email,
      attachedDocumentId,
      attachedDocumentName,
      sessionId
    };

    // Clear any existing timeout
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current);
    }

    // Set up timeout handler (30 seconds)
    // Since all queries are now async, we shouldn't hit this timeout often
    // But keeping it as a safeguard
    queryTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        console.log('Query timeout detected after 30 seconds');
        setIsTyping(false);
        queryTimeoutRef.current = null;

        // Since all queries are async, this message is simpler
        const timeoutMessage: ChatMessage = {
          id: `msg-${Date.now()}-timeout`,
          text: `Your question is taking a bit longer to process. The response should arrive shortly.

If you continue to experience delays, please try refreshing the page.`,
          sender: 'cal',
          timestamp: new Date(),
          markdown: true
        };

        addMessage({
          text: timeoutMessage.text,
          sender: timeoutMessage.sender,
          timestamp: timeoutMessage.timestamp
        });
      }
    }, 30000); // 30 second timeout

    // Use WebSocket if connected
    if (isConnected) {
      try {
        sendWebSocketMessage(text, context);
        // Response will come through handleWebSocketMessage
        // Note: handleWebSocketMessage should clear the timeout
      } catch (err) {
        console.error('WebSocket send failed:', err);
        if (queryTimeoutRef.current) {
          clearTimeout(queryTimeoutRef.current);
          queryTimeoutRef.current = null;
        }
        setIsTyping(false);
      }
    } else {
      // Fallback to REST API
      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(`${API_ENDPOINT}/cal/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            message: text,
            sessionId,
            context
          })
        });

        const data = await response.json();

        // Clear timeout when REST response arrives
        if (queryTimeoutRef.current) {
          clearTimeout(queryTimeoutRef.current);
          queryTimeoutRef.current = null;
        }

        const calMessage: ChatMessage = {
          id: `msg-${Date.now()}-cal`,
          text: data.response || 'I apologize, but I encountered an error processing your request.',
          sender: 'cal',
          timestamp: new Date(),
          markdown: true
        };

        addMessage({
          text: calMessage.text,
          sender: calMessage.sender,
          timestamp: calMessage.timestamp
        });

        // If this was a gap analysis, update the document status and store the results
        if (attachedDocumentId && text.toLowerCase().includes('analyze')) {
          // Check if the response contains gap analysis results
          if (data.response && (data.response.includes('Critical') || data.response.includes('Major') || data.response.includes('Minor'))) {
            setUploadedDocuments(prev =>
              prev.map(d => d.documentId === attachedDocumentId
                ? { ...d, status: 'analyzed', gapAnalysis: data }
                : d
              )
            );
          }
        }

        setIsTyping(false);
      } catch (error) {
        console.error('Error sending message (REST):', error);
        const errorMessage: ChatMessage = {
          id: `msg-${Date.now()}-error`,
          text: 'I apologize, but I encountered an error. Please try again.',
          sender: 'cal',
          timestamp: new Date()
        };

        addMessage({
          text: errorMessage.text,
          sender: errorMessage.sender,
          timestamp: errorMessage.timestamp
        });
        setIsTyping(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim()) {
      const message = inputMessage.trim();

      // Check if user is asking to update a document
      if (message.toLowerCase().includes('update') &&
          (message.toLowerCase().includes('document') || message.toLowerCase().includes('changes'))) {

        // Find if there's an analyzed document that can be updated
        const analyzedDoc = uploadedDocuments.find(d => d.status === 'analyzed' && d.gapAnalysis);

        if (analyzedDoc) {
          // Send the message first
          await sendMessage(message);
          setInputMessage('');

          // Then offer to update the document
          setTimeout(() => {
            const suggestionMessage: ChatMessage = {
              id: `msg-${Date.now()}-suggestion`,
              text: `I found ${analyzedDoc.fileName} has been analyzed with gaps identified. Would you like me to generate an updated version with all the recommended changes applied? You can click the "Update" button next to the document in the left panel.`,
              sender: 'cal',
              timestamp: new Date()
            };
            addMessage({
              text: suggestionMessage.text,
              sender: suggestionMessage.sender,
              timestamp: suggestionMessage.timestamp
            });
          }, 1000);
          return;
        }
      }

      await sendMessage(message);
      setInputMessage('');
    }
  };

  const uploadDocument = async (file: File): Promise<string> => {
    try {
      const token = await getAccessTokenSilently();

      // Get presigned URL
      const urlResponse = await fetch(`${API_ENDPOINT}/cal/upload-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          fileName: file.name,
          fileType: file.type || 'application/octet-stream'
        })
      });

      const { uploadUrl, fields, documentId } = await urlResponse.json();

      // Track document
      const newDoc: UploadedDocument = {
        documentId,
        fileName: file.name,
        uploadTime: new Date(),
        status: 'uploading'
      };

      setUploadedDocuments(prev => [...prev, newDoc]);

      // Upload to S3
      // IMPORTANT: For S3 presigned POST, the file MUST be the last field
      const formData = new FormData();

      // Debug: Log the fields we're sending
      console.log('Presigned POST URL:', uploadUrl);
      console.log('Presigned POST fields:', fields);

      // Add all fields from presigned POST first
      // The 'key' field must come first for S3
      if (fields.key) {
        formData.append('key', fields.key);
      }

      // Add all other fields
      Object.keys(fields).forEach(key => {
        if (key !== 'key') {  // Skip 'key' since we already added it
          formData.append(key, fields[key]);
        }
      });

      // File must be added LAST for S3 presigned POST to work
      formData.append('file', file);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        // Try to get error details from S3
        const errorText = await uploadResponse.text();
        console.error('S3 Upload Error Response:', errorText);
        console.error('S3 Status:', uploadResponse.status);
        throw new Error('Upload failed');
      }

      // Update status
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'uploaded' }
          : d
        )
      );

      // Set this as the active document for the session
      setActiveDocumentId(documentId);
      setActiveDocumentName(file.name);

      return documentId;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const analyzeDocument = async (documentId: string) => {
    // Send analysis request through chat/WebSocket instead of direct API call
    // This handles long-running analysis without timeout issues
    await sendMessage(`Please analyze the uploaded document for compliance gaps against API/MPMS standards.`, documentId);
    return;

    // OLD CODE - keeping for reference but not using due to timeout issues
    try {
      const token = await getAccessTokenSilently();

      // Update status to analyzing
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'analyzing' }
          : d
        )
      );

      const response = await fetch(`${API_ENDPOINT}/cal/analyze-gaps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          documentId
        })
      });

      const analysis = await response.json();

      // Update with analysis results
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'analyzed', gapAnalysis: analysis }
          : d
        )
      );

      return analysis;
    } catch (error) {
      console.error('Error analyzing document:', error);
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'uploaded' }
          : d
        )
      );
    }
  };

  const showGapSelectionDialog = (documentId: string) => {
    setSelectedGapsDialog({ open: true, documentId });
  };

  const updateDocumentWithChanges = async (documentId: string, selectedGaps?: string[]) => {
    try {
      const token = await getAccessTokenSilently();
      const doc = uploadedDocuments.find(d => d.documentId === documentId);
      if (!doc || !doc.gapAnalysis) return;

      // If no selected gaps provided, use all gaps
      if (!selectedGaps) {
        const allGaps: Gap[] = [
          ...(doc.gapAnalysis.gaps?.critical || []),
          ...(doc.gapAnalysis.gaps?.major || []),
          ...(doc.gapAnalysis.gaps?.minor || [])
        ];
        selectedGaps = allGaps.map(g => g.gap_id);
      }

      // Update status to updating
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'updating', selectedGaps }
          : d
        )
      );

      // Filter gaps to only selected ones
      const selectedGapObjects = [
        ...(doc.gapAnalysis.gaps?.critical || []),
        ...(doc.gapAnalysis.gaps?.major || []),
        ...(doc.gapAnalysis.gaps?.minor || [])
      ].filter(gap => selectedGaps?.includes(gap.gap_id));

      // Request Cal to generate updated document with only selected fixes
      const response = await fetch(`${API_ENDPOINT}/cal/update-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          documentId,
          fileName: doc.fileName,
          selectedGaps: selectedGapObjects,
          gapAnalysis: doc.gapAnalysis
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update document');
      }

      const result = await response.json();

      // Show track changes preview if available
      if (result.trackChangesPreview) {
        setTrackChangesDialog({ open: true, preview: result.trackChangesPreview });
      }

      // Update with new document info
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? {
              ...d,
              status: 'updated',
              updatedDocumentId: result.updatedDocumentId,
              updatedDocumentUrl: result.downloadUrl,
              trackChangesPreview: result.trackChangesPreview
            }
          : d
        )
      );

      // Send a message to chat about the update
      const updateMessage: ChatMessage = {
        id: `msg-${Date.now()}-update`,
        text: `✅ I've successfully applied ${selectedGaps?.length} selected fixes to ${doc.fileName}. The document now addresses the compliance gaps you selected. You can preview the changes or download the updated version.`,
        sender: 'cal',
        timestamp: new Date()
      };
      addMessage({
        text: updateMessage.text,
        sender: updateMessage.sender,
        timestamp: updateMessage.timestamp
      });

      return result;
    } catch (error) {
      console.error('Error updating document:', error);
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'analyzed' }
          : d
        )
      );

      // Send error message to chat
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        text: 'I encountered an error while trying to update the document. Please try again.',
        sender: 'cal',
        timestamp: new Date()
      };
      addMessage({
        text: errorMessage.text,
        sender: errorMessage.sender,
        timestamp: errorMessage.timestamp
      });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const documentId = await uploadDocument(file);

        // Just upload the document, don't auto-analyze
        // User can now ask questions or provide context before analysis
        const uploadMessage: ChatMessage = {
          id: `msg-${Date.now()}-upload`,
          text: `Document "${file.name}" uploaded successfully. You can now ask me questions about it or request a gap analysis.`,
          sender: 'cal',
          timestamp: new Date()
        };
        addMessage({
          text: uploadMessage.text,
          sender: uploadMessage.sender,
          timestamp: uploadMessage.timestamp
        });

      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      try {
        const documentId = await uploadDocument(file);

        // Just upload the document, don't auto-analyze
        // User can now ask questions or provide context before analysis
        const uploadMessage: ChatMessage = {
          id: `msg-${Date.now()}-upload`,
          text: `Document "${file.name}" uploaded successfully. You can now ask me questions about it or request a gap analysis.`,
          sender: 'cal',
          timestamp: new Date()
        };
        addMessage({
          text: uploadMessage.text,
          sender: uploadMessage.sender,
          timestamp: uploadMessage.timestamp
        });

      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'updated':
        return <CheckCircleIcon color="success" />;
      case 'analyzed':
        return <CheckCircleIcon color="success" />;
      case 'updating':
      case 'analyzing':
        return <CircularProgress size={20} />;
      case 'uploaded':
        return <DocumentIcon color="primary" />;
      default:
        return <CloudUploadIcon />;
    }
  };

  return (
    <>
      <Grid container spacing={3}>
      {/* Left Panel - Documents */}
      <Grid item xs={12} md={3}>
        <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Documents for Analysis
          </Typography>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            Upload Document
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.docx,.doc,.txt"
            onChange={handleFileSelect}
          />

          <List>
            {uploadedDocuments.map((doc) => (
              <ListItem key={doc.documentId}>
                <ListItemAvatar>
                  {getStatusIcon(doc.status)}
                </ListItemAvatar>
                <ListItemText
                  primary={doc.fileName}
                  secondary={
                    doc.status === 'updating' ? 'Applying changes...' :
                    doc.status === 'updated' ? 'Updated document ready' :
                    doc.status === 'analyzing' ? 'Analyzing gaps...' :
                    doc.status === 'analyzed' ? 'Analysis complete' :
                    doc.status === 'uploaded' ? 'Ready to analyze' : 'Uploading...'
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {doc.status === 'uploaded' && (
                    <Button
                      size="small"
                      onClick={() => analyzeDocument(doc.documentId)}
                    >
                      Analyze
                    </Button>
                  )}
                  {doc.status === 'analyzed' && doc.gapAnalysis && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => updateDocumentWithChanges(doc.documentId)}
                      variant="outlined"
                      color="primary"
                    >
                      Update
                    </Button>
                  )}
                  {doc.status === 'updated' && doc.updatedDocumentUrl && (
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={doc.updatedDocumentUrl}
                      target="_blank"
                      variant="contained"
                      color="success"
                    >
                      Download
                    </Button>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>

          {uploadedDocuments.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Upload procedures or operational documents for Cal to analyze against API/MPMS standards
            </Alert>
          )}
        </Paper>
      </Grid>

      {/* Center Panel - Chat */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ height: '70vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Clear Chat Button - subtle, positioned in top right */}
          <Tooltip title="Clear Chat">
            <IconButton
              onClick={() => clearMessages()}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main'
                }
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              position: 'relative'
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {isDragging && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}
              >
                <Typography variant="h6" color="primary">
                  Drop document here for gap analysis
                </Typography>
              </Box>
            )}

            <List>
              {messages.map((message) => (
                <ListItem key={message.id} sx={{ flexDirection: 'column', alignItems: message.sender === 'cal' ? 'flex-start' : 'flex-end' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', maxWidth: '80%' }}>
                    {message.sender === 'cal' && (
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        <img
                          src={getCalAvatar().path}
                          alt="Cal"
                          style={{
                            width: 72,
                            height: 72,
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                        />
                      </Avatar>
                    )}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        backgroundColor: message.sender === 'cal' ? 'grey.50' : 'primary.50',
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          marginTop: 1,
                          marginBottom: 1
                        },
                        '& th, & td': {
                          border: '1px solid',
                          borderColor: 'divider',
                          padding: '8px',
                          textAlign: 'left'
                        },
                        '& th': {
                          backgroundColor: 'grey.200',
                          fontWeight: 'bold'
                        },
                        '& code': {
                          backgroundColor: 'grey.200',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          fontSize: '0.9em'
                        },
                        '& p': {
                          margin: 0,
                          marginBottom: '0.5em'
                        },
                        '& p:last-child': {
                          marginBottom: 0
                        }
                      }}
                    >
                      {message.attachedDocument && (
                        <Chip
                          size="small"
                          icon={<AttachFileIcon />}
                          label={message.attachedDocument.fileName}
                          sx={{ mb: 1 }}
                        />
                      )}
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{message.text}</ReactMarkdown>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}

              {isTyping && (
                <ListItem>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                      <img
                        src={getCalAvatar().path}
                        alt="Cal"
                        style={{
                          width: 72,
                          height: 72,
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                      />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {loadingMessage}
                    </Typography>
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  </Box>
                </ListItem>
              )}
            </List>
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Show active document indicator */}
          {activeDocumentId && (
            <Box sx={{ p: 1, px: 2, bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon fontSize="small" />
                Active Document: {activeDocumentName}
                <IconButton
                  size="small"
                  sx={{ color: 'white', ml: 'auto' }}
                  onClick={() => {
                    setActiveDocumentId(null);
                    setActiveDocumentName(null);
                  }}
                >
                  ×
                </IconButton>
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
            <IconButton
              color="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <AttachFileIcon />
            </IconButton>
            <TextField
              fullWidth
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={activeDocumentId
                ? `Ask Cal about ${activeDocumentName}...`
                : "Ask Cal about compliance gaps, API standards..."}
              variant="outlined"
              size="small"
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', pb: 1.5, px: 2, mt: -1, color: 'text.secondary' }}
          >
            Cal can make mistakes. Check important info.
          </Typography>
        </Paper>
      </Grid>

      {/* Right Panel - Gap Reports */}
      <Grid item xs={12} md={3}>
        <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Gap Analysis Reports
          </Typography>

          {/* Generated PDF Reports */}
          {generatedReports.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {generatedReports.map(report => (
                <Card key={report.id} sx={{ mb: 2, bgcolor: 'background.paper' }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom noWrap title={report.title}>
                          {report.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                          <Chip
                            size="small"
                            label={report.type.replace('_', ' ').toUpperCase()}
                            color={report.type === 'gap_analysis' ? 'warning' : 'primary'}
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {report.status === 'ready' && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              size="small"
                              label="Ready"
                              color="success"
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {report.generatedAt.toLocaleString()}
                        </Typography>
                        {report.pages && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            • {report.pages} page{report.pages > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          // Open PDF in new tab for download
                          window.open(report.pdfUrl, '_blank');
                        }}
                        variant="contained"
                        fullWidth
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Existing uploaded documents analysis */}
          {uploadedDocuments
            .filter(doc => (doc.status === 'analyzed' || doc.status === 'updated') && doc.gapAnalysis)
            .map(doc => (
              <Card key={doc.documentId} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {doc.fileName}
                  </Typography>

                  {doc.status === 'updated' && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Compliance Issues Fixed"
                      color="success"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}

                  {doc.gapAnalysis?.gaps?.summary && doc.status !== 'updated' && (
                    <Box sx={{ mt: 1 }}>
                      {doc.gapAnalysis.gaps.summary.critical_count > 0 && (
                        <Chip
                          icon={<ErrorIcon />}
                          label={`${doc.gapAnalysis.gaps.summary.critical_count} Critical`}
                          color="error"
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      )}
                      {doc.gapAnalysis.gaps.summary.major_count > 0 && (
                        <Chip
                          icon={<WarningIcon />}
                          label={`${doc.gapAnalysis.gaps.summary.major_count} Major`}
                          color="warning"
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      )}
                      {doc.gapAnalysis.gaps.summary.minor_count > 0 && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`${doc.gapAnalysis.gaps.summary.minor_count} Minor`}
                          color="success"
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<AssessmentIcon />}
                      onClick={() => {
                        const msg = `Show me the detailed gap analysis for ${doc.fileName}`;
                        sendMessage(msg);
                      }}
                    >
                      View Details
                    </Button>
                    {doc.status === 'updated' && doc.updatedDocumentUrl && (
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        color="success"
                        href={doc.updatedDocumentUrl}
                        target="_blank"
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}

          {uploadedDocuments.filter(d => d.status === 'analyzed').length === 0 && (
            <Alert severity="info">
              Gap reports will appear here after document analysis
            </Alert>
          )}
        </Paper>
      </Grid>
      </Grid>

      {/* Gap Selection Dialog */}
    <Dialog
      open={selectedGapsDialog.open}
      onClose={() => setSelectedGapsDialog({ open: false })}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Select Gaps to Fix
        <Typography variant="body2" color="text.secondary">
          Choose which compliance gaps Cal should address in the updated document
        </Typography>
      </DialogTitle>
      <DialogContent>
        {(() => {
          const doc = uploadedDocuments.find(d => d.documentId === selectedGapsDialog.documentId);
          if (!doc?.gapAnalysis) return null;

          const handleToggle = (gapId: string) => {
            setSelectedGapsForFix(prev =>
              prev.includes(gapId)
                ? prev.filter(id => id !== gapId)
                : [...prev, gapId]
            );
          };

          const selectAll = (severity: 'critical' | 'major' | 'minor') => {
            const gaps = doc.gapAnalysis.gaps?.[severity] || [];
            const gapIds = gaps.map((g: Gap) => g.gap_id);
            setSelectedGapsForFix(prev => {
              const combined = [...prev, ...gapIds];
              return Array.from(new Set(combined));
            });
          };

          const deselectAll = (severity: 'critical' | 'major' | 'minor') => {
            const gaps = doc.gapAnalysis.gaps?.[severity] || [];
            const gapIds = gaps.map((g: Gap) => g.gap_id);
            setSelectedGapsForFix(prev => prev.filter(id => !gapIds.includes(id)));
          };

          return (
            <>
              {/* Critical Gaps */}
              {doc.gapAnalysis.gaps?.critical?.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                      <Typography sx={{ flexGrow: 1 }}>Critical Gaps ({doc.gapAnalysis.gaps.critical.length})</Typography>
                      <Button size="small" onClick={() => selectAll('critical')}>Select All</Button>
                      <Button size="small" onClick={() => deselectAll('critical')}>Deselect All</Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {doc.gapAnalysis.gaps.critical.map((gap: Gap) => (
                      <FormControlLabel
                        key={gap.gap_id}
                        control={
                          <Checkbox
                            checked={selectedGapsForFix.includes(gap.gap_id)}
                            onChange={() => handleToggle(gap.gap_id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle2">{gap.description}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Standard: {gap.standard} | Fix: {gap.recommendation}
                            </Typography>
                          </Box>
                        }
                        sx={{ display: 'block', mb: 2 }}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Major Gaps */}
              {doc.gapAnalysis.gaps?.major?.length > 0 && (
                <Accordion>
                  <AccordionSummary>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                      <Typography sx={{ flexGrow: 1 }}>Major Gaps ({doc.gapAnalysis.gaps.major.length})</Typography>
                      <Button size="small" onClick={() => selectAll('major')}>Select All</Button>
                      <Button size="small" onClick={() => deselectAll('major')}>Deselect All</Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {doc.gapAnalysis.gaps.major.map((gap: Gap) => (
                      <FormControlLabel
                        key={gap.gap_id}
                        control={
                          <Checkbox
                            checked={selectedGapsForFix.includes(gap.gap_id)}
                            onChange={() => handleToggle(gap.gap_id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle2">{gap.description}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Standard: {gap.standard} | Fix: {gap.recommendation}
                            </Typography>
                          </Box>
                        }
                        sx={{ display: 'block', mb: 2 }}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Minor Gaps */}
              {doc.gapAnalysis.gaps?.minor?.length > 0 && (
                <Accordion>
                  <AccordionSummary>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Typography sx={{ flexGrow: 1 }}>Minor Gaps ({doc.gapAnalysis.gaps.minor.length})</Typography>
                      <Button size="small" onClick={() => selectAll('minor')}>Select All</Button>
                      <Button size="small" onClick={() => deselectAll('minor')}>Deselect All</Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {doc.gapAnalysis.gaps.minor.map((gap: Gap) => (
                      <FormControlLabel
                        key={gap.gap_id}
                        control={
                          <Checkbox
                            checked={selectedGapsForFix.includes(gap.gap_id)}
                            onChange={() => handleToggle(gap.gap_id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle2">{gap.description}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Standard: {gap.standard} | Fix: {gap.recommendation}
                            </Typography>
                          </Box>
                        }
                        sx={{ display: 'block', mb: 2 }}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              <DialogActions>
                <Button onClick={() => setSelectedGapsDialog({ open: false })}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    updateDocumentWithChanges(selectedGapsDialog.documentId!, selectedGapsForFix);
                    setSelectedGapsDialog({ open: false });
                    setSelectedGapsForFix([]);
                  }}
                  disabled={selectedGapsForFix.length === 0}
                >
                  Apply {selectedGapsForFix.length} Selected Fixes
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>

    {/* Track Changes Preview Dialog */}
    <Dialog
      open={trackChangesDialog.open}
      onClose={() => setTrackChangesDialog({ open: false })}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Track Changes Preview
        <Typography variant="body2" color="text.secondary">
          Review the changes Cal will make to your document
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '60vh', overflow: 'auto' }}>
          <Typography
            component="pre"
            sx={{
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              '& del': { color: 'error.main', textDecoration: 'line-through' },
              '& ins': { color: 'success.main', fontWeight: 'bold' }
            }}
          >
            {trackChangesDialog.preview || 'No preview available'}
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTrackChangesDialog({ open: false })}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CalComplianceTab;